import { useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  List,
  Select,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { Calendar, X, CalendarDays, CalendarCheck } from "lucide-react";

import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  getPastEvents,
  getUpcomingEvents,
} from "../../actionCreator/calendarActionCreator";
import { eventStyles } from "./calendarData";
import { formatDueDate } from "../../utils";

import { iconMap } from "./AddEditEvent";
import AttractiveEmptyState from "../../common/AttractiveEmptyState";
const { RangePicker } = DatePicker;

const { Text } = Typography;

const UpcomingEventsPanel = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [drawerActiveTab, setDrawerActiveTab] = useState("upcoming");

  const defaultUpcomingRange = [
    dayjs().startOf("day"),
    dayjs().add(30, "day").endOf("day"),
  ];

  const defaultPastRange = [
    dayjs().subtract(30, "day").startOf("day"),
    dayjs().endOf("day"),
  ];

  const [upcomingFilters, setUpcomingFilters] = useState({
    range: defaultUpcomingRange,
    type: null,
  });

  const [pastFilters, setPastFilters] = useState({
    range: defaultPastRange,
    type: null,
  });

  const upcomingEvents = useSelector(
    (state) => state.calendarReducer.upcomingEvents,
  );
  const pastEvents = useSelector((state) => state.calendarReducer.pastEvents);
  const eventTypes = useSelector((state) => state.calendarReducer.eventTypes);

  const sortedEvents = [...upcomingEvents].sort(
    (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
  );
  const sortedPastEvents = [...pastEvents].sort(
    (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
  );

  const handleUpcomingFilterChange = (key, value) => {
    if (key === "range") {
      setUpcomingFilters((prev) => ({
        ...prev,
        range: value ?? defaultUpcomingRange,
      }));
    } else {
      setUpcomingFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handlePastFilterChange = (key, value) => {
    if (key === "range") {
      setPastFilters((prev) => ({
        ...prev,
        range: value ?? defaultPastRange,
      }));
    } else {
      setPastFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const getActionIcon = (type) => {
    if (!type) return null;

    const icon = iconMap[type.trim()];
    const color = eventStyles[type.trim()];

    if (!icon || !color) return null;

    return (
      <div
        style={{
          backgroundColor: color?.backgroundColor || "#f8fafc",
          color: color?.color || "#64748b",
        }}
        className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-slate-100/50"
      >
        <div className="tw-h-3.5 tw-w-3.5 tw-flex tw-items-center tw-justify-center">{icon}</div>
      </div>
    );
  };

  const getStatusTag = (status) => {
    const map = {
      COMPLETED: { color: "#16a34a", bg: "#dcfce7", label: "Completed" },
      PENDING: { color: "#ca8a04", bg: "#fef9c3", label: "Pending" },
      schedule: { color: "#2563eb", bg: "#dbeafe", label: "Schedule" },
    };

    const data = map[status] || map.PENDING;

    return (
      <Tag
        style={{
          background: data.bg,
          color: data.color,
        }}
        className="!tw-border-0 !tw-rounded-md !tw-px-2 !tw-py-0.5 tw-text-[10px] tw-font-bold tw-m-0"
      >
        {data.label}
      </Tag>
    );
  };

  useEffect(() => {
    const [start, end] = upcomingFilters.range;
    dispatch(
      getUpcomingEvents({
        startDate: start,
        endDate: end,
        type: upcomingFilters.type,
      }),
    );
  }, [upcomingFilters, dispatch]);

  useEffect(() => {
    const [start, end] = pastFilters.range;
    dispatch(
      getPastEvents({
        startDate: start,
        endDate: end,
        type: pastFilters.type,
      }),
    );
  }, [pastFilters, dispatch]);

  const options = [
    ...eventTypes
      ?.filter((i) =>
        ["Birthday", "Anniversary", "Meeting"]?.includes(i?.typeName),
      )
      ?.map((d) => ({
        label: d?.typeName,
        value: d?.calendarEventTypeId,
      })),
    {
      label: "Tasks",
      options: eventTypes
        ?.filter(
          (i) => !["Birthday", "Anniversary", "Meeting"]?.includes(i?.typeName),
        )
        ?.map((d) => ({
          label: d?.typeName,
          value: d?.calendarEventTypeId,
        })),
    },
  ];

  return (
    <>
      <Button
        type="text"
        className="!tw-h-[38px] !tw-rounded-xl tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-text-slate-600 hover:tw-text-slate-800 hover:tw-bg-slate-50 tw-font-bold tw-text-xs tw-px-4 tw-transition-all tw-duration-200"
        onClick={() => setOpen(true)}
      >
        All Events
      </Button>

      <Drawer
        title={null}
        placement="right"
        onClose={() => setOpen(false)}
        open={open}
        width={380}
        closable={false}
        className="events-drawer"
      >
        <div className="tw-bg-slate-50 tw-h-full tw-p-6 tw-flex tw-flex-col">
          <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
            <div className="tw-flex tw-flex-col tw-gap-1">
              <h3 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-0">All Events</h3>
              <span className="tw-text-xs tw-text-slate-400">View and manage all your scheduled events</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="tw-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-full tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-slate-700 tw-cursor-pointer tw-transition-colors tw-border-none"
            >
              <X size={16} />
            </button>
          </div>

          <div className="tw-bg-slate-100/70 tw-p-1 tw-rounded-xl tw-flex tw-w-full tw-mb-6 tw-border tw-border-solid tw-border-slate-200/30">
            <button
              onClick={() => setDrawerActiveTab("upcoming")}
              className={`tw-flex-1 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-transition-all tw-duration-200 tw-cursor-pointer tw-border-0 focus:tw-outline-none ${
                drawerActiveTab === "upcoming"
                  ? "tw-text-white tw-bg-theme tw-shadow-[0_2px_8px_rgba(93,68,255,0.15)]"
                  : "tw-text-slate-500 tw-bg-transparent hover:tw-text-slate-800 hover:tw-bg-white/40"
              }`}
            >
              Upcoming events
            </button>
            <button
              onClick={() => setDrawerActiveTab("past")}
              className={`tw-flex-1 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-transition-all tw-duration-200 tw-cursor-pointer tw-border-0 focus:tw-outline-none ${
                drawerActiveTab === "past"
                  ? "tw-text-white tw-bg-theme tw-shadow-[0_2px_8px_rgba(93,68,255,0.15)]"
                  : "tw-text-slate-500 tw-bg-transparent hover:tw-text-slate-800 hover:tw-bg-white/40"
              }`}
            >
              Past events
            </button>
          </div>

          <div className={`tw-flex-1 ${(drawerActiveTab === "upcoming" && sortedEvents.length > 0) || (drawerActiveTab === "past" && sortedPastEvents.length > 0) ? "tw-overflow-y-auto" : "tw-overflow-visible"}`}>
            {drawerActiveTab === "upcoming" ? (
              <>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-5">
                  <RangePicker
                    value={upcomingFilters.range}
                    hoverPreview={false}
                    needConfirm={true}
                    onChange={(val) =>
                      handleUpcomingFilterChange("range", val)
                    }
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                    className="tw-flex-1 tw-range-picker-modern !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-400 !tw-h-[40px] tw-bg-white focus:!tw-border-theme"
                  />

                  <Select
                    placeholder="Select type"
                    allowClear
                    value={upcomingFilters.type}
                    onChange={(val) =>
                      handleUpcomingFilterChange("type", val)
                    }
                    className="tw-w-[110px] tw-select-modern"
                    popupClassName="tw-rounded-xl"
                    options={options}
                  />
                </div>
                
                <List
                  dataSource={sortedEvents}
                  className={`tw-max-h-[calc(100vh-280px)] ${sortedEvents.length > 0 ? "tw-overflow-y-auto" : "tw-overflow-visible"}`}
                  locale={{
                    emptyText: (
                      <AttractiveEmptyState
                        icon={CalendarDays}
                        title="No upcoming events"
                        description="Upcoming tasks and meetings will show up here."
                      />
                    ),
                  }}
                  renderItem={(event) => {
                    const type = eventTypes?.find(
                      (i) =>
                        i?.calendarEventTypeId ===
                        event?.calendarEventTypeId,
                    )?.typeName;

                    return (
                      <div
                        key={event.id}
                        className="tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-p-4 tw-rounded-2xl tw-mb-2.5 last:tw-mb-0 tw-shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:tw-shadow-[0_8px_20px_rgba(93,68,255,0.04)] tw-transition-all tw-duration-250 tw-flex tw-items-center tw-gap-3.5"
                      >
                        {getActionIcon(type)}
                        <div className="tw-flex-1 tw-min-w-0">
                          <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-1">
                            <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-truncate">
                              {event.title}
                            </span>
                            {getStatusTag(event.status)}
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-normal tw-text-slate-400">
                            <Calendar size={10} />
                            <span>{formatDueDate(event.dueDate)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </>
            ) : (
              <>
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-5">
                  <RangePicker
                    hoverPreview={false}
                    needConfirm={true}
                    value={pastFilters.range}
                    onChange={(val) => handlePastFilterChange("range", val)}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    className="tw-flex-1 tw-range-picker-modern !tw-rounded-xl !tw-border-slate-200 hover:!tw-border-slate-400 !tw-h-[40px] tw-bg-white focus:!tw-border-theme"
                  />

                  <Select
                    placeholder="Select type"
                    allowClear
                    value={pastFilters.type}
                    onChange={(val) => handlePastFilterChange("type", val)}
                    options={options}
                    className="tw-w-[110px] tw-select-modern"
                    popupClassName="tw-rounded-xl"
                  />
                </div>
                
                <List
                  dataSource={sortedPastEvents}
                  className={`tw-max-h-[calc(100vh-280px)] ${sortedPastEvents.length > 0 ? "tw-overflow-y-auto" : "tw-overflow-visible"}`}
                  locale={{
                    emptyText: (
                      <AttractiveEmptyState
                        icon={CalendarCheck}
                        title="No past events"
                        description="No past event history found for the selected filter."
                      />
                    ),
                  }}
                  renderItem={(event) => {
                    const type = eventTypes?.find(
                      (i) =>
                        i?.calendarEventTypeId ===
                        event?.calendarEventTypeId,
                    )?.typeName;

                    return (
                      <div
                        key={event.id}
                        className="tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-p-4 tw-rounded-2xl tw-mb-2.5 last:tw-mb-0 tw-shadow-[0_2px_8px_rgba(0,0,0,0.01)] hover:tw-shadow-[0_8px_20px_rgba(93,68,255,0.04)] tw-transition-all tw-duration-250 tw-flex tw-items-center tw-gap-3.5"
                      >
                        {getActionIcon(type)}
                        <div className="tw-flex-1 tw-min-w-0">
                          <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-1">
                            <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-truncate">
                              {event.title}
                            </span>
                            {getStatusTag(event.status)}
                          </div>
                          <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-normal tw-text-slate-400">
                            <Calendar size={10} />
                            <span>{formatDueDate(event.dueDate)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default UpcomingEventsPanel;
